import { AxiosError } from "axios";
import { httpTokenCommon } from "../helper/http-common";
import {
  PortalAccountSummary,
  SalesManagerOption,
  SalesManagerProfile,
  SalesManagerRegisterPayload,
  SalesManagerRepSummary,
  SalesManagerSyncResult,
  SalesManagerSyncStatus,
} from "../types/salesManagerEnrolment";

const normalizeManager = (raw: Record<string, unknown>): SalesManagerOption => ({
  salesManagerCode: String(raw.salesManagerCode ?? raw.SalesManagerCode ?? "").trim(),
  salesManagerName: String(raw.salesManagerName ?? raw.SalesManagerName ?? "").trim(),
});

const normalizeRep = (raw: Record<string, unknown>): SalesManagerRepSummary => ({
  salesRepCode: String(raw.salesRepCode ?? raw.SalesRepCode ?? "").trim(),
  salesRepName: String(raw.salesRepName ?? raw.SalesRepName ?? "").trim(),
  repRole: String(raw.repRole ?? raw.RepRole ?? "").trim() || undefined,
  salesManagerCode: String(raw.salesManagerCode ?? raw.SalesManagerCode ?? "").trim(),
  salesManagerName: String(raw.salesManagerName ?? raw.SalesManagerName ?? "").trim(),
});

const normalizePortalAccount = (raw: Record<string, unknown>): PortalAccountSummary => ({
  exists: Boolean(raw.exists ?? raw.Exists),
  userName: String(raw.userName ?? raw.UserName ?? "").trim(),
  mustChangePassword: Boolean(raw.mustChangePassword ?? raw.MustChangePassword),
});

const normalizeProfile = (raw: Record<string, unknown>): SalesManagerProfile => {
  const portalRaw = (raw.portalAccount ?? raw.PortalAccount ?? {}) as Record<string, unknown>;
  const repsRaw = Array.isArray(raw.assignedReps ?? raw.AssignedReps)
    ? (raw.assignedReps ?? raw.AssignedReps as unknown[])
    : [];

  return {
    salesManagerCode: String(raw.salesManagerCode ?? raw.SalesManagerCode ?? "").trim(),
    salesManagerName: String(raw.salesManagerName ?? raw.SalesManagerName ?? "").trim(),
    portalAccount: normalizePortalAccount(portalRaw),
    assignedReps: repsRaw
      .map((item) => normalizeRep(item as Record<string, unknown>))
      .filter((rep) => rep.salesRepCode),
  };
};

const normalizeSyncPayload = (raw: Record<string, unknown>): SalesManagerSyncResult => ({
  status: String(raw.status ?? raw.Status ?? "").trim(),
  description: String(raw.description ?? raw.Description ?? "").trim() || undefined,
  userName: String(raw.userName ?? raw.UserName ?? "").trim() || undefined,
  tempPassword: String(raw.tempPassword ?? raw.TempPassword ?? "").trim() || undefined,
  mustChangePassword: Boolean(raw.mustChangePassword ?? raw.MustChangePassword),
});

const isNotFound = (error: unknown): boolean =>
  (error as AxiosError)?.response?.status === 404;

/** Match manager codes like "007" vs "7" when comparing search input to Sage list. */
const managerCodesMatch = (a: string, b: string): boolean => {
  const left = a.trim();
  const right = b.trim();
  if (!left || !right) return false;
  if (left === right) return true;
  const leftNum = Number(left);
  const rightNum = Number(right);
  if (!Number.isNaN(leftNum) && !Number.isNaN(rightNum) && leftNum === rightNum) return true;
  return false;
};

const syncStatusFromProfile = (
  code: string,
  profile: SalesManagerProfile
): SalesManagerSyncStatus => {
  if (profile.portalAccount.exists) {
    return {
      status: "Synced",
      description: "A portal account already exists for this manager.",
      userName: profile.portalAccount.userName || code,
    };
  }
  return {
    status: "Eligible (Not Synced)",
    description: "Manager exists but has no portal account yet.",
  };
};

class SalesManagerEnrolmentService {
  /** Admin: distinct managers from synced Sage data */
  async getSalesManagers(): Promise<SalesManagerOption[]> {
    const response = await httpTokenCommon.get("auth/sales-managers");
    const list = Array.isArray(response.data) ? response.data : [];
    return list
      .map((item) => normalizeManager(item as Record<string, unknown>))
      .filter((manager) => manager.salesManagerCode);
  }

  private findManagerInList(
    managers: SalesManagerOption[],
    salesManagerCode: string
  ): SalesManagerOption | undefined {
    return managers.find((m) => managerCodesMatch(m.salesManagerCode, salesManagerCode));
  }

  /**
   * Lightweight fallback when profile API is not deployed yet: header only from the
   * existing managers list. Does not call sales-reps (Admin-only, separate enrolment flow).
   */
  private async buildProfileFromManagersList(
    salesManagerCode: string
  ): Promise<SalesManagerProfile | null> {
    const managers = await this.getSalesManagers();
    const manager = this.findManagerInList(managers, salesManagerCode.trim());
    if (!manager) return null;

    return {
      salesManagerCode: manager.salesManagerCode,
      salesManagerName: manager.salesManagerName,
      portalAccount: {
        exists: false,
        userName: manager.salesManagerCode,
        mustChangePassword: false,
      },
      assignedReps: [],
    };
  }

  async getProfile(salesManagerCode: string): Promise<SalesManagerProfile> {
    const code = salesManagerCode.trim();
    try {
      const response = await httpTokenCommon.get(
        `auth/sales-managers/${encodeURIComponent(code)}`,
        { silent: true }
      );
      return normalizeProfile((response.data ?? {}) as Record<string, unknown>);
    } catch (error) {
      if (!isNotFound(error)) throw error;
      const fallback = await this.buildProfileFromManagersList(code);
      if (!fallback) throw error;
      return fallback;
    }
  }

  async getSyncStatus(
    salesManagerCode: string,
    profileHint?: SalesManagerProfile
  ): Promise<SalesManagerSyncStatus> {
    const code = salesManagerCode.trim();
    try {
      const response = await httpTokenCommon.get("auth/sales-managers/syncstatus", {
        params: { salesManagerCode: code },
        silent: true,
      });
      const raw = (response.data ?? {}) as Record<string, unknown>;
      return {
        status: String(raw.status ?? raw.Status ?? "").trim(),
        description: String(raw.description ?? raw.Description ?? "").trim() || undefined,
        userName: String(raw.userName ?? raw.UserName ?? "").trim() || undefined,
      };
    } catch (error) {
      if (!isNotFound(error)) throw error;
      const profile = profileHint ?? (await this.buildProfileFromManagersList(code));
      if (!profile) {
        return {
          status: "Not found",
          description: "No sales manager with this code exists in Sage.",
        };
      }
      return syncStatusFromProfile(code, profile);
    }
  }

  async syncManager(salesManagerCode: string) {
    return httpTokenCommon.post(
      `auth/sales-managers/sync?salesManagerCode=${encodeURIComponent(salesManagerCode.trim())}`,
      undefined
    );
  }

  async syncManagerNormalized(salesManagerCode: string): Promise<SalesManagerSyncResult> {
    const response = await this.syncManager(salesManagerCode);
    return normalizeSyncPayload((response.data ?? {}) as Record<string, unknown>);
  }

  /** Admin: create a SalesManager portal account linked to a Sage manager code */
  registerSalesManager(payload: SalesManagerRegisterPayload) {
    return httpTokenCommon.post("auth/register-salesmanager", payload);
  }

  /** SalesManager: reps assigned to the logged-in manager (from synced VW_SALESREP_FULL) */
  async getMyReps(): Promise<SalesManagerRepSummary[]> {
    const response = await httpTokenCommon.get("auth/sales-managers/my-reps", {
      silent: true,
    });
    const list = Array.isArray(response.data)
      ? response.data
      : Array.isArray((response.data as { items?: unknown[] })?.items)
        ? (response.data as { items: unknown[] }).items
        : [];
    return list
      .map((item) => normalizeRep(item as Record<string, unknown>))
      .filter((rep) => rep.salesRepCode);
  }
}

const salesManagerEnrolmentService = new SalesManagerEnrolmentService();
export default salesManagerEnrolmentService;
